import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { getActivePartnerProductPrices } from '@apis/partner';
import type { PageResponse } from '@models/api';
import type { PartnerProductPriceResponse } from '@models/partner';
import type { GetProductsParams } from '@apis/product';
import { dropEmptyValues } from '@utils/common';

type Params = {
  partnerId: number;
  page: number;
  pageSize: number;
  searchParam: GetProductsParams;
};

const FILTER_KEYS: Array<keyof GetProductsParams> = [
  'productName',
  'countryId',
  'stockId',
  'productStatus',
  'productSaleType',
];

export const useActivePartnerProductPrices = ({
  partnerId,
  page,
  pageSize,
  searchParam,
}: Params) => {
  const cleaned = dropEmptyValues(searchParam, FILTER_KEYS);

  const queryOptions: UseQueryOptions<PageResponse<PartnerProductPriceResponse>, Error> = {
    queryKey: ['activePartnerProductPrices', partnerId, page, pageSize, cleaned],
    queryFn: () =>
      getActivePartnerProductPrices(partnerId, {
        pageNo: page,
        pageSize,
        ...cleaned,
      }),
    enabled: partnerId > 0,

    // React Query v5 replacement for keepPreviousData
    // Keeps the previous page data during pagination / filter transitions for smoother UX.
    placeholderData: (prev) => prev,
  };

  return useQuery(queryOptions);
};
